import { Injectable } from '@angular/core';

/**
 * Draws a red count-circle badge over the top-right of the browser
 * tab's favicon (canvas overlay technique). Common pattern: gmail
 * / slack / linear all use the same visual so operators recognize
 * "unread" at a glance without reading the title. Mike 2026-07-03
 * (ref: https://stackoverflow.com/questions/65719387).
 *
 * Base favicon precedence:
 *   1. captureBase() called explicitly by whoever set the customer
 *      favicon (HostBrandService.ready$ subscriber, etc). Preferred —
 *      guarantees we're compositing over the RESOLVED per-customer
 *      icon, not the default we happened to see first.
 *   2. On first setBadge() with no captured base, read the current
 *      href off the icon <link>. Fine for admin.f2-tech.ai where the
 *      favicon is a static build asset.
 *
 * State machine:
 *   - _baseHref caches the "clean" favicon we return to when badge=0.
 *   - _lastBadgeHref tracks our own rendered output so captureBase()
 *     doesn't accidentally treat a previous badge render as the base.
 *   - _currentBadge is the last-applied count; setBadge is a no-op
 *     if unchanged.
 *
 * Failure modes:
 *   - Cross-origin favicon (http:// image w/o CORS): canvas.toDataURL
 *     throws SecurityError. Caught → we leave the favicon as-is and
 *     rely on the ♪N title prefix. HostBrandService inlines the
 *     customer favicon as a data URL so this branch is only hit on
 *     the neutral F2 default asset (which IS same-origin, so OK).
 */
@Injectable({ providedIn: 'root' })
export class FaviconBadgeService {
  private _baseHref: string | null = null;
  private _lastBadgeHref: string | null = null;
  private _currentBadge = 0;
  // Output size — 32×32 is the largest common browser render target
  // for tab favicons. Rendering at a fixed size gives a sharper
  // overlay than scaling whatever size the source uses.
  private readonly SIZE = 32;

  /**
   * Remember the current icon <link> href as the "base" we'll return
   * to when badge=0. Callers should invoke this after applying a new
   * per-customer favicon so the badge composites over the correct
   * icon (rather than the pre-brand default).
   */
  captureBase(): void {
    if (typeof document === 'undefined') return;
    const link = this._iconLink(false);
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href) return;
    // Skip our own badge renders — capturing one as the base would
    // permanently bake the red circle into future renders.
    if (this._lastBadgeHref && href === this._lastBadgeHref) return;
    this._baseHref = href;
    // Re-render if a badge is currently active so the new base picks
    // up its overlay immediately.
    if (this._currentBadge > 0) void this.setBadge(this._currentBadge);
  }

  /**
   * Apply / clear the badge overlay. 0 restores the last known base
   * favicon. > 0 renders a red circle with the count (1..9 or 9+).
   * Fire-and-forget — async under the hood but callers don't need
   * to await.
   */
  async setBadge(count: number): Promise<void> {
    if (typeof document === 'undefined') return;
    const n = Math.max(0, Math.floor(Number(count) || 0));
    // Cheap no-op — matches the current state.
    if (n === this._currentBadge && n === 0) return;
    this._currentBadge = n;
    const link = this._iconLink(true);
    if (!link) return;
    if (n === 0) {
      // Restore the base if we know it. If we don't (badge was applied
      // before any capture), leave the current href in place — better
      // to have SOME favicon than a broken empty one.
      if (this._baseHref) link.setAttribute('href', this._baseHref);
      this._lastBadgeHref = null;
      return;
    }
    // Best-effort: seed _baseHref from the current href when nobody
    // called captureBase() first (e.g. static build favicon).
    if (!this._baseHref) {
      const href = link.getAttribute('href');
      if (href) this._baseHref = href;
    }
    if (!this._baseHref) return;
    try {
      const badged = await this._render(this._baseHref, n);
      if (!badged) return;
      link.setAttribute('href', badged);
      this._lastBadgeHref = badged;
    } catch { /* silent — favicon overlay is a nicety */ }
  }

  /**
   * Locate the icon <link>. Prefers rel="icon" over rel="shortcut
   * icon"; injects a fresh one when neither exists (create=true) so
   * we can still overlay on pages that forgot to declare an icon.
   */
  private _iconLink(create: boolean): HTMLLinkElement | null {
    const links = document.head.querySelectorAll('link[rel]');
    // Prefer the plain "icon" rel over "shortcut icon" / apple-touch.
    let best: HTMLLinkElement | null = null;
    let bestScore = -1;
    for (let i = 0; i < links.length; i++) {
      const el = links[i] as HTMLLinkElement;
      const rels = (el.getAttribute('rel') || '').toLowerCase().split(/\s+/);
      let score = -1;
      if (rels.includes('icon')) score = 3;
      else if (rels.includes('shortcut')) score = 2;
      else if (rels.includes('apple-touch-icon')) score = 1;
      if (score > bestScore) { best = el; bestScore = score; }
    }
    if (best) return best;
    if (!create) return null;
    const el = document.createElement('link');
    el.setAttribute('rel', 'icon');
    document.head.appendChild(el);
    return el;
  }

  private async _render(baseHref: string, count: number): Promise<string> {
    const img = new Image();
    // crossOrigin='anonymous' lets us composite same-origin AND
    // cross-origin favicons served with CORS headers. Data URLs
    // (our per-customer inline case) don't need it but setting the
    // attribute is harmless.
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('base favicon load failed'));
      img.src = baseHref;
    });
    const canvas = document.createElement('canvas');
    canvas.width = this.SIZE;
    canvas.height = this.SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    // Base — scale to full canvas so 16px favicons render sharp too.
    ctx.drawImage(img, 0, 0, this.SIZE, this.SIZE);

    const label = count >= 10 ? '9+' : String(count);
    // Two-digit labels ('9+') need a slightly wider pill; single
    // digits use a tight circle.
    const twoChars = label.length >= 2;
    const badgeR = this.SIZE * 0.36;
    const badgeW = twoChars ? badgeR * 2.2 : badgeR * 2;
    const badgeH = badgeR * 2;
    const cx = this.SIZE - badgeW / 2 - 0.5;
    const cy = this.SIZE - badgeH / 2 - 0.5;
    // Rounded rect pill (works for both circle + wide-pill cases).
    ctx.fillStyle = '#dc2626';   // red-600 — matches the sidenav badge.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    this._roundedRect(ctx, cx - badgeW / 2, cy - badgeH / 2, badgeW, badgeH, badgeR);
    ctx.fill();
    ctx.stroke();
    // Count label — bold + white for max readability against the red.
    ctx.fillStyle = '#ffffff';
    const fontSize = Math.floor(badgeH * (twoChars ? 0.62 : 0.72));
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Nudge y down by 1 so the visual center matches the geometric
    // center — most fonts have more ascender than descender.
    ctx.fillText(label, cx, cy + 1);

    try {
      return canvas.toDataURL('image/png');
    } catch {
      // Cross-origin taint — canvas can't export. Give up on this
      // render pass; a subsequent captureBase() with a data URL will
      // succeed. Return '' so the caller doesn't overwrite the icon
      // with an empty href.
      return '';
    }
  }

  private _roundedRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
  ): void {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.arcTo(x + w, y, x + w, y + rr, rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
    ctx.lineTo(x + rr, y + h);
    ctx.arcTo(x, y + h, x, y + h - rr, rr);
    ctx.lineTo(x, y + rr);
    ctx.arcTo(x, y, x + rr, y, rr);
    ctx.closePath();
  }
}
